import React, { useState, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import Quiz from './Quiz';
import { getQuizForLevel } from '../quizQuestions';
import StorySection from './StorySection';
import AudioButton from './AudioButton';
import ReflectionCheckpoint from './ReflectionCheckpoint';
import CommunityExamples from './CommunityExamples';
import GentleReflection from './GentleReflection';
import { getStory, hasRealStory } from '../stories';
import { getPillar } from '../pillars';

function LearningView({ level, onStartCoding, onMarkComplete, onNextLevel, currentLevelIndex, totalLevels }) {
  const [currentSection, setCurrentSection] = useState(0);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizPassed, setQuizPassed] = useState(false);

  const isLastLevel = currentLevelIndex === totalLevels - 1;

  // Reset state when level changes
  useEffect(() => {
    setCurrentSection(0);
    setShowQuiz(false);
    setQuizPassed(false);
  }, [currentLevelIndex]);

  const sections = level.learningContent?.sections || [];
  const quizQuestions = getQuizForLevel(level.id);
  const story = getStory(level.id);
  const showStory = currentSection === 0 && story && hasRealStory(level.id);

  const handleNext = () => {
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
    } else {
      // After completing all sections, show quiz if available
      if (quizQuestions.length > 0) {
        setShowQuiz(true);
      } else {
        onStartCoding();
      }
    }
  };

  const handlePrevious = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  const handleQuizComplete = (passed, score, total) => {
    if (passed) {
      setQuizPassed(true);
      // Mark level as complete when quiz is passed
      if (onMarkComplete) {
        onMarkComplete();
      }
    } else {
      // Return to learning content for review
      setShowQuiz(false);
      setCurrentSection(0);
    }
  };

  const section = sections[currentSection];
  const isLastSection = currentSection === sections.length - 1;

  // If quiz passed, show reflection and navigation
  if (quizPassed) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="mb-6 p-6 bg-green-900/30 border border-green-600/30 rounded-lg">
          <h2 className="text-2xl font-bold text-green-300 mb-2">
            ✓ Level Complete!
          </h2>
          <p className="text-slate-300">
            You've demonstrated understanding of this concept. {!isLastLevel && 'Continue to the next level when ready.'}
          </p>
        </div>

        {/* Reflection checkpoint - pause before moving on */}
        <ReflectionCheckpoint question={story?.reflectionPrompt} />

        {/* Navigation Options */}
        <div className="mt-8 p-6 bg-slate-800 rounded-lg border border-slate-700">
          <h3 className="text-xl font-bold mb-3">Ready for the Next Level?</h3>
          {!isLastLevel ? (
            <button
              onClick={onNextLevel}
              className="w-full px-8 py-4 bg-green-600 hover:bg-green-700 rounded-lg font-semibold text-lg transition"
            >
              Next Level →
            </button>
          ) : (
            <p className="text-slate-400 text-center">
              🎉 Congratulations! You've completed all levels. Try the Practice Mode or Learning Circles from the Dashboard.
            </p>
          )}
        </div>
      </div>
    );
  }

  // If quiz should be shown, render quiz component
  if (showQuiz && quizQuestions.length > 0) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-white mb-2">
            📝 Knowledge Check
          </h2>
          <p className="text-slate-400">
            Test your understanding before moving to the coding challenge. You need 70% to pass.
          </p>
        </div>
        <Quiz
          questions={quizQuestions}
          onComplete={handleQuizComplete}
          levelId={level.id}
        />
      </div>
    );
  }

  // Get pillar metadata for this level
  const primaryPillar = level.pillar?.primary ? getPillar(level.pillar.primary) : null;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Pillar Badge - Shows which literacy this level develops */}
      {primaryPillar && (
        <div className="mb-4 flex justify-end">
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${primaryPillar.bgColor} ${primaryPillar.borderColor} border`}>
            <span className="text-lg">{primaryPillar.icon}</span>
            <span className={`text-sm font-medium ${primaryPillar.textColor}`}>
              {primaryPillar.shortName}
            </span>
          </div>
        </div>
      )}

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-slate-400">
            Section {currentSection + 1} of {sections.length}
          </span>
          <span className="text-sm text-slate-400">
            {Math.round(((currentSection + 1) / sections.length) * 100)}% Complete
          </span>
        </div>
        <div className="w-full bg-slate-700 rounded-full h-2">
          <div
            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentSection + 1) / sections.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Story Section - Only on first section if story exists */}
      {showStory && <StorySection story={story} />}

      {/* Learning Content */}
      <div className="bg-slate-800 rounded-lg p-8 shadow-lg">
        {/* Section Title with Audio Button */}
        <div className="mb-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex-1">
              <h2 className="text-3xl font-bold text-white mb-2">
                {section?.title}
              </h2>
              {section?.duration && (
                <p className="text-slate-400 text-sm">
                  📚 {section.duration} min read
                </p>
              )}
            </div>
            {/* Audio Button - Listen to this section */}
            <AudioButton
              text={`${section?.title}. ${section?.content?.map(block => {
                if (block.type === 'paragraph') return block.text;
                if (block.type === 'heading') return block.text;
                if (block.type === 'note' || block.type === 'tip' || block.type === 'warning') return block.text;
                return '';
              }).join(' ')}`}
              label="🔊 Listen"
            />
          </div>
        </div>

        {/* Section Content */}
        <div className="prose prose-invert max-w-none">
          {section?.content?.map((block, idx) => {
            switch (block.type) {
              case 'paragraph':
                return (
                  <p key={idx} className="text-slate-200 text-lg leading-relaxed mb-4">
                    {block.text}
                  </p>
                );

              case 'heading':
                return (
                  <h3 key={idx} className="text-xl font-semibold text-white mt-6 mb-3">
                    {block.text}
                  </h3>
                );

              case 'list':
                return (
                  <ul key={idx} className="list-disc list-inside space-y-2 mb-4 text-slate-200">
                    {block.items.map((item, i) => (
                      <li key={i} className="ml-4">{item}</li>
                    ))}
                  </ul>
                );

              case 'code':
                return (
                  <div key={idx} className="my-6">
                    {block.title && (
                      <p className="text-sm text-slate-400 mb-2">{block.title}</p>
                    )}
                    <div className="bg-slate-900 rounded-lg overflow-hidden border border-slate-700">
                      <Editor
                        height="150px"
                        defaultLanguage="python"
                        theme="vs-dark"
                        value={block.code}
                        options={{
                          readOnly: true,
                          minimap: { enabled: false },
                          scrollBeyondLastLine: false,
                          fontSize: 14,
                          lineNumbers: 'on',
                        }}
                      />
                    </div>
                    {block.output && (
                      <div className="mt-2 bg-black rounded-lg p-3 border border-slate-700">
                        <p className="text-xs text-slate-400 mb-1">Output:</p>
                        <pre className="text-green-400 text-sm">{block.output}</pre>
                      </div>
                    )}
                    {block.explanation && (
                      <p className="text-slate-300 text-sm mt-2 italic">
                        💡 {block.explanation}
                      </p>
                    )}
                  </div>
                );

              case 'note':
                return (
                  <div key={idx} className="bg-blue-900/30 border-l-4 border-blue-500 p-4 my-4 rounded">
                    <p className="text-blue-200">
                      <span className="font-semibold">📌 Note: </span>
                      {block.text}
                    </p>
                  </div>
                );

              case 'tip':
                return (
                  <div key={idx} className="bg-green-900/30 border-l-4 border-green-500 p-4 my-4 rounded">
                    <p className="text-green-200">
                      <span className="font-semibold">💡 Tip: </span>
                      {block.text}
                    </p>
                  </div>
                );

              case 'warning':
                return (
                  <div key={idx} className="bg-yellow-900/30 border-l-4 border-yellow-500 p-4 my-4 rounded">
                    <p className="text-yellow-200">
                      <span className="font-semibold">⚠️ Warning: </span>
                      {block.text}
                    </p>
                  </div>
                );

              case 'keyPoints':
                return (
                  <div key={idx} className="bg-slate-700/50 rounded-lg p-5 my-6">
                    <h4 className="text-lg font-semibold text-white mb-3">
                      🎯 Key Points
                    </h4>
                    <ul className="space-y-2">
                      {block.points.map((point, i) => (
                        <li key={i} className="flex items-start text-slate-200">
                          <span className="text-blue-400 mr-2">✓</span>
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );

              default:
                return null;
            }
          })}
        </div>
      </div>

      {/* Community Examples - Collapsed by default */}
      <CommunityExamples
        levelId={level.id}
        pillarKey={level.pillar?.primary}
      />

      {/* Gentle Reflection - Ambient awareness from community patterns */}
      <GentleReflection
        levelId={level.id}
        pillarKey={level.pillar?.primary}
      />

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-6">
        <button
          onClick={handlePrevious}
          disabled={currentSection === 0}
          className={`px-6 py-3 rounded-lg font-semibold transition ${
            currentSection === 0
              ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
              : 'bg-slate-700 text-white hover:bg-slate-600'
          }`}
        >
          ← Previous
        </button>

        <button
          onClick={handleNext}
          className="px-6 py-3 rounded-lg font-semibold bg-blue-600 hover:bg-blue-700 text-white transition"
        >
          {isLastSection
            ? (quizQuestions.length > 0 ? '📝 Take Quiz →' : '🚀 Start Coding →')
            : 'Next →'}
        </button>
      </div>

      {/* Section Navigator */}
      <div className="mt-6 flex justify-center gap-2">
        {sections.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentSection(idx)}
            className={`w-3 h-3 rounded-full transition ${
              idx === currentSection
                ? 'bg-blue-500'
                : idx < currentSection
                ? 'bg-green-500'
                : 'bg-slate-600'
            }`}
            title={`Section ${idx + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

export default LearningView;
